import React from "react";
import { commitMutation, graphql, type GraphQLTaggedNode } from "react-relay";
import type { RouteComponentProps } from "react-router";
import { Link, withRouter } from "react-router-dom";

import {
	faArchive,
	faArrowUp,
	faBan,
	faEllipsisV,
	faHandPointRight,
	faShare,
	faTrashCan,
	faUserEdit,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { get } from "lodash";
import moment from "moment";
import { ConnectionHandler } from "relay-runtime";

import { warning } from "~/lib/prompts";
import { commitMutationPromise, smart } from "~/lib/relayUtil";
import { environment } from "~/models";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";
import Avatar from "~/ui/legacy/Avatar";
import IconButton from "~/ui/legacy/IconButton";
import { __globalModalContext } from "~/ui/modals";
import toast from "~/ui/toast";

import FeedbackContext from "../FeedbackContext";
import { FeedbackRequestSubject, FeedbackSubject, FeedbackTopicSkeleton } from "../FeedbackSubject";
import NudgeRequestees from "../NudgeRequestees";
import { FeedbackFilter, feedbackFilterToGraphQL } from "../utils";

import type { FeedbackCardCancelFurtherResponsesMutation } from "~/__generated__/FeedbackCardCancelFurtherResponsesMutation.graphql";
import type { FeedbackCardDeleteDraftMutation } from "~/__generated__/FeedbackCardDeleteDraftMutation.graphql";
import type { FeedbackCardFeedback$data } from "~/__generated__/FeedbackCardFeedback.graphql";
import type { FeedbackCardItem$data } from "~/__generated__/FeedbackCardItem.graphql";
import type { FeedbackCardRequest$data } from "~/__generated__/FeedbackCardRequest.graphql";
import type { FeedbackCardRestoreRequestMutation } from "~/__generated__/FeedbackCardRestoreRequestMutation.graphql";

import styles from "./css/FeedbackCard.module.css";

export const MarkAsComplete = graphql`
	mutation FeedbackCardMarkAsCompleteMutation($id: ID!) {
		markAsComplete(id: $id) {
			feedbackAndRequestsEdge {
				node {
					...FeedbackCardFeedback
				}
			}
		}
	}
`;

export const MarkAsPriority = graphql`
	mutation FeedbackCardMarkAsPriorityMutation($id: ID!) {
		markAsPriority(id: $id) {
			feedbackAndRequestsEdge {
				node {
					...FeedbackCardFeedback
				}
			}
		}
	}
`;

const CancelFurtherResponses = graphql`
	mutation FeedbackCardCancelFurtherResponsesMutation($id: ID!) {
		cancelFurtherResponses(id: $id) {
			feedback {
				...FeedbackCardFeedback
			}
		}
	}
`;

const DismissRequest = graphql`
	mutation FeedbackCardDismissRequestMutation($id: ID!) {
		dismissRequest(id: $id) {
			feedbackAndRequestsEdge {
				node {
					...FeedbackCardRequest
				}
			}
		}
	}
`;

const RestoreRequest = graphql`
	mutation FeedbackCardRestoreRequestMutation($id: ID!) {
		restoreRequest(id: $id) {
			feedbackAndRequestsEdge {
				node {
					...FeedbackCardRequest
				}
			}
		}
	}
`;

const DeleteDraft = graphql`
	mutation FeedbackCardDeleteDraftMutation($id: ID!) {
		deleteDraftFeedback(id: $id) {
			deletedId @deleteRecord
		}
	}
`;

const STATUSES = {
	REQUEST: { colorClass: styles.statusFilledBlue, display: "Request" },
	ALL_RESPONDED: { colorClass: styles.statusGreen, display: "All Responded" },
	IN_PROGRESS: { colorClass: styles.statusFilledYellow, display: "In Progress" },
	DRAFT: { colorClass: styles.statusBasic, display: "Draft" },
	DRAFT_REQUEST: { colorClass: styles.statusYellow, display: "Draft Request" },
	DRAFT_RESPONSE: { colorClass: styles.statusBlue, display: "Draft Response" },
	RECEIVED: { colorClass: styles.statusLightBlue, display: "Received" },
	ARCHIVED: { colorClass: styles.statusGrey, display: "Archived" },
	SENT: { colorClass: styles.statusGrey, display: "Sent" },
	DISMISSED: { colorClass: styles.statusGrey, display: "Dismissed" },
	CANCELLED: { colorClass: styles.statusGrey, display: "Expired" },
};

export const FeedbackCardSkeleton = () => (
	<div className={cx(styles.wrapper, styles.wrapperSkeleton)}>
		<div className={styles.dot} />
		<FeedbackTopicSkeleton small />

		<div className={cx(styles.status, styles.statusSkeleton)} />
		<div className={cx(styles.responses, styles.responsesSkeleton)} />
		<div className={cx(styles.updated, styles.updatedSkeleton)} />

		<FontAwesomeIcon icon={faCircle} className={styles.circleSkeleton} />
		<FontAwesomeIcon icon={faEllipsisV} />
	</div>
);

interface FeedbackOrRequestRendererProps {
	item: FeedbackCardItem$data;
	user: {
		id: string;
		fullName: string;
	};
	className?: string;
	link?: boolean;
	isSpecific?: boolean;
	hideMenu?: boolean;
}

const FeedbackOrRequestRenderer = smart(
	class FeedbackOrRequestRenderer extends React.PureComponent<FeedbackOrRequestRendererProps> {
		static Fragments = {
			item: graphql`
				fragment FeedbackCardItem on FeedbackOrRequest {
					__typename

					...FeedbackCardFeedback
					...FeedbackCardRequest
				}
			`,
		};

		static defaultProps = {
			link: true,
			isSpecific: false,
			hideMenu: false,
		};

		render() {
			const { item, user, ...rest } = this.props;
			const { __typename } = item;

			if (__typename === "FeedbackNode") {
				return <FeedbackCard user={user} feedback={item} {...rest} />;
			}
			if (__typename === "FeedbackRequestNode") {
				return <FeedbackRequestCard user={user} request={item} {...rest} />;
			}
			throw new Error(`FeedbackOrRequestRenderer got unexpected __typename: ${__typename}`);
		}
	},
);

interface FeedbackRequestCardProps {
	request: FeedbackCardRequest$data;
	user: {
		id: string;
		fullName: string;
	};
	className?: string;
	link?: boolean;
	isSpecific?: boolean;
	hideMenu?: boolean;
}

const FeedbackRequestCard = smart(
	class FeedbackRequestCard extends React.PureComponent<FeedbackRequestCardProps> {
		static Fragments = {
			request: graphql`
				fragment FeedbackCardRequest on FeedbackRequestNode {
					...FeedbackSubjectRequest_item

					id
					hasDraftResponse
					isArchived
					cancelledByUser
					cancelledByCreator
					lastSeenByUser
					feedbackRecord {
						id
					}
					feedback {
						...FeedbackCardFeedback
					}
				}
			`,
		};

		static defaultProps = {
			link: true,
			isSpecific: false,
			hideMenu: false,
		};

		render() {
			const { request, user, ...rest } = this.props;
			const { feedback } = request;

			return <FeedbackCard request={request} feedback={feedback} user={user} {...rest} />;
		}
	},
);

interface Config {
	key: string;
	filters: {
		filter: string;
		priority: boolean;
	};
}

interface FireMutation {
	id: string;
	mutation: GraphQLTaggedNode;
	addConfig: Config;
	removeConfig: Config;
	context: React.ContextType<typeof FeedbackContext>;
}

function fireMutation({ id, mutation, addConfig, removeConfig }: FireMutation) {
	commitMutation<FeedbackCardRestoreRequestMutation>(environment, {
		mutation,
		variables: { id },
		configs: [
			{
				type: "RANGE_ADD",
				edgeName: "feedbackAndRequestsEdge",
				parentID: "viewer",
				connectionInfo: [
					{
						...addConfig,
						rangeBehavior: "prepend",
					},
				],
			},
		],
		updater: store => {
			const viewer = store.get("viewer");
			if (!viewer) return;

			const connection = ConnectionHandler.getConnection(viewer, removeConfig.key, removeConfig.filters);
			if (connection) ConnectionHandler.deleteNode(connection, id);
		},
		onCompleted: (_data, err) => {
			if (err) toast.error(`Could not restore the feedback request: ${err}`);
		},
	});
}

interface FeedbackCardProps extends RouteComponentProps<{ filter: string }> {
	feedback: FeedbackCardFeedback$data;
	user: {
		id: string;
		fullName: string;
	};
	className?: string;
	request?: Omit<FeedbackCardRequest$data, " $fragmentType">;
	link?: boolean;
	isSpecific?: boolean;
	hideMenu?: boolean;
}

interface FeedbackCardState {
	dropdownOpen: boolean;
}

interface FeedbackListFilters {
	filter: keyof typeof FeedbackFilter;
	priority: boolean;
	excludeThreeSixtyChildren: boolean;
}

class FeedbackCardClass extends React.PureComponent<FeedbackCardProps, FeedbackCardState> {
	state = {
		dropdownOpen: false,
	};

	static contextType = FeedbackContext;
	declare context: React.ContextType<typeof FeedbackContext>;

	static Fragments = {
		feedback: graphql`
			fragment FeedbackCardFeedback on FeedbackNode {
				...FeedbackSubject_item

				id
				title
				type
				isArchived
				isDraft
				lastSeenByUser
				isMeetingFeedback
				requestCount
				responseCount
				sharedWithMe
				updatedAt
				isThreeSixtyParent
				isAnonymous

				creator {
					id
					email
					image
					fullName
					firstName
					lastName
				}

				recipients {
					shared
					user {
						id
					}
				}
			}
		`,
	};

	get isUnsolicitedGiven() {
		const { feedback, user } = this.props;
		const { type, creator, recipients } = feedback;

		return (
			type === "UNSOLICITED" &&
			creator &&
			user.id === creator.id &&
			(!recipients || !recipients.some(({ user }) => user.id === creator.id))
		);
	}

	onDropdownOpenChange = (open: boolean) => {
		this.setState({ dropdownOpen: open });
	};

	getConfigs = (): {
		priority: { key: "PriorityList_priority"; filters: FeedbackListFilters };
		archived: {
			key: "EverythingElseList_everythingElse";
			filters: FeedbackListFilters;
		};
	} => {
		const { filter } = this.context;
		return {
			priority: {
				key: "PriorityList_priority",
				filters: { filter: feedbackFilterToGraphQL(filter), priority: true, excludeThreeSixtyChildren: true },
			},
			archived: {
				key: "EverythingElseList_everythingElse",
				filters: { filter: feedbackFilterToGraphQL(filter), priority: false, excludeThreeSixtyChildren: true },
			},
		};
	};

	markAsComplete = () => {
		const { id } = this.props.feedback;
		const { priority, archived } = this.getConfigs();

		fireMutation({
			id,
			mutation: MarkAsComplete,
			addConfig: archived,
			removeConfig: priority,
			context: this.context,
		});
	};

	markAsPriority = () => {
		const { id } = this.props.feedback;
		const { priority, archived } = this.getConfigs();

		fireMutation({
			id,
			mutation: MarkAsPriority,
			addConfig: priority,
			removeConfig: archived,
			context: this.context,
		});
	};

	deleteDraft = () => {
		const { id, isDraft } = this.props.feedback;

		if (!isDraft) return;

		warning("Deleting a draft cannot be undone.", {
			yesProps: { variant: "destructive" },
			noProps: { variant: "secondary" },
			yesText: "Delete",
			noText: "No, take me back",
			title: "Delete draft?",
		}).then(res => {
			if (!res) return;

			commitMutation<FeedbackCardDeleteDraftMutation>(environment, {
				mutation: DeleteDraft,
				variables: { id },
				onCompleted: (response, err) => {
					if (err) {
						toast.error(`Unable to delete draft: ${err[0].message}`);
						return;
					}

					toast.success("Draft deleted");
				},
				onError: () => toast.error("Unable to delete draft!"),
			});
		});
	};

	nudgeRespondents = () =>
		__globalModalContext?.open(({ close }) => (
			<NudgeRequestees id={this.props.feedback.id} onClose={close} selected={this.props.feedback.isAnonymous} />
		));

	cancelFurtherResponses = async () => {
		const { feedback } = this.props;

		const res = await warning("Cancelling further responses is a permanent action.", {
			title: "Are you sure?",
			yesProps: { variant: "destructive" },
			noProps: { variant: "secondary" },
			yesText: "Yes",
			noText: "No",
		});

		if (!res) return;

		try {
			const { cancelFurtherResponses } = await commitMutationPromise<FeedbackCardCancelFurtherResponsesMutation>(
				environment,
				{
					mutation: CancelFurtherResponses,
					variables: { id: feedback.id },
				},
			);

			if (cancelFurtherResponses) {
				toast.success("Successfully cancelled further responses");
			} else {
				toast.error("Failed to cancel further responses");
			}
		} catch (error) {
			if (Array.isArray(error)) {
				toast.error(`Failed to cancel further responses: ${error[0].message}`);
			}
		}
	};

	addRecipients = () => {
		const { feedback, history } = this.props;
		if (feedback.isDraft) return;

		if (feedback.isThreeSixtyParent) {
			return history.push(`/feedback/three-sixty/${feedback.id}/edit/`);
		}

		return history.push(`/feedback/${feedback.id}/edit/`, { field: "ask" });
	};

	shareWithOthers = () => {
		const { feedback, history } = this.props;
		if (feedback.isDraft) return;

		if (feedback.isThreeSixtyParent) {
			return history.push(`/feedback/three-sixty/${feedback.id}/edit/`);
		}

		return history.push(`/feedback/${feedback.id}/edit/`, { field: "share" });
	};

	dismissRequest = () => {
		const { request } = this.props;
		if (!request) return;

		const { id, feedbackRecord } = request;
		if (feedbackRecord) return;

		const { priority, archived } = this.getConfigs();

		fireMutation({
			id,
			mutation: DismissRequest,
			addConfig: archived,
			removeConfig: priority,
			context: this.context,
		});
	};

	restoreRequest = () => {
		const { request } = this.props;
		if (!request) return;

		const { id, feedbackRecord } = request;
		if (feedbackRecord) return;

		const { priority, archived } = this.getConfigs();

		fireMutation({
			id,
			mutation: RestoreRequest,
			addConfig: priority,
			removeConfig: archived,
			context: this.context,
		});
	};

	getStatus = () => {
		const { feedback, request, user } = this.props;
		const { isArchived, isMeetingFeedback, isDraft, type, requestCount, responseCount, creator, recipients } = feedback;

		if (type === "UNSOLICITED" || isMeetingFeedback) {
			if (creator && creator.id === user.id && !recipients.some(({ user }) => user.id === creator.id)) {
				return "SENT";
			} else {
				return "RECEIVED";
			}
		}

		if (type === "REQUESTED" && request) {
			if (request.feedbackRecord) return "SENT";
			if (request.cancelledByUser) return "DISMISSED";
			if (request.cancelledByCreator) return "CANCELLED";
			if (request.hasDraftResponse) return "DRAFT_RESPONSE";
			return "REQUEST";
		}

		if (isArchived) return "ARCHIVED";

		if (isDraft) {
			if (type === "REQUESTED") return "DRAFT_REQUEST";
			return "DRAFT";
		}

		if (type === "REQUESTED") {
			if (requestCount === responseCount) return "ALL_RESPONDED";
			return "IN_PROGRESS";
		}

		return "";
	};

	renderStatus() {
		const { colorClass, display } = get(STATUSES, this.getStatus(), { colorClass: "", display: "--" });
		return <div className={cx(styles.status, colorClass)}>{display}</div>;
	}

	renderResponses() {
		const { request, feedback } = this.props;
		const { requestCount, responseCount, type, isDraft, isMeetingFeedback } = feedback;
		const noResponses = request || type !== "REQUESTED" || isDraft;

		if (isMeetingFeedback) {
			return <div className={styles.responses}>{responseCount}</div>;
		}

		let text = `${responseCount}/${requestCount}`;
		if (noResponses) text = "\u2014";

		return <div className={noResponses ? styles.noResponses : styles.responses}>{text}</div>;
	}

	renderDropdownItems() {
		const { request, feedback, user } = this.props;
		const { type, isArchived, isDraft, requestCount, responseCount, creator } = feedback;

		if (request) {
			if (request.isArchived) {
				return (
					<DropdownItem
						key="restore"
						icon={<DropdownItem.Icon className={styles.dropdownIcon} icon={faArrowUp} />}
						className={styles.dropdownItem}
						onClick={this.restoreRequest}
					>
						Restore request
					</DropdownItem>
				);
			}
			return (
				<DropdownItem
					key="dismiss"
					icon={<DropdownItem.Icon className={styles.dropdownIcon} icon={faBan} />}
					className={styles.dropdownItem}
					onClick={this.dismissRequest}
				>
					Dismiss request
				</DropdownItem>
			);
		}

		const shareWithOthers = (
			<DropdownItem
				key="share"
				icon={<DropdownItem.Icon className={styles.dropdownIcon} icon={faShare} />}
				className={styles.dropdownItem}
				onClick={this.shareWithOthers}
			>
				Share with…
			</DropdownItem>
		);

		if (isDraft) {
			return (
				<DropdownItem
					key="delete"
					icon={<DropdownItem.Icon className={styles.dropdownIcon} icon={faTrashCan} />}
					className={styles.dropdownItem}
					onClick={this.deleteDraft}
				>
					Delete draft
				</DropdownItem>
			);
		}

		if (isArchived) {
			return (
				<>
					{(type === "REQUESTED" || !this.isUnsolicitedGiven) && (
						<DropdownItem
							key="unarchive"
							icon={<DropdownItem.Icon className={styles.dropdownIcon} icon={faArchive} />}
							className={styles.dropdownItem}
							onClick={this.markAsPriority}
						>
							Unarchive
						</DropdownItem>
					)}
					{creator && creator.id === user.id && type !== "UNSOLICITED" && shareWithOthers}
				</>
			);
		}

		return (
			<>
				<DropdownItem
					key="archive"
					icon={<DropdownItem.Icon className={styles.dropdownIcon} icon={faArchive} />}
					className={styles.dropdownItem}
					onClick={this.markAsComplete}
				>
					Archive
				</DropdownItem>

				{creator && creator.id === user.id && (
					<>
						{responseCount < requestCount && (
							<>
								<DropdownItem
									key="nudge"
									icon={<DropdownItem.Icon className={styles.dropdownIcon} icon={faHandPointRight} />}
									className={styles.dropdownItem}
									onClick={this.nudgeRespondents}
								>
									Remind respondents…
								</DropdownItem>

								<DropdownItem
									key="cancel further responses"
									icon={<DropdownItem.Icon className={styles.dropdownIcon} icon={faBan} />}
									className={styles.dropdownItem}
									onClick={this.cancelFurtherResponses}
								>
									Cancel further responses
								</DropdownItem>
							</>
						)}
						{type !== "UNSOLICITED" && (
							<>
								<DropdownItem
									key="add"
									icon={<DropdownItem.Icon className={styles.dropdownIcon} icon={faUserEdit} />}
									className={styles.dropdownItem}
									onClick={this.addRecipients}
								>
									Edit recipients…
								</DropdownItem>
								{shareWithOthers}
							</>
						)}
					</>
				)}
			</>
		);
	}

	renderDropdown() {
		const { dropdownOpen } = this.state;

		if (this.isUnsolicitedGiven) {
			return null;
		}

		return (
			<ArrowDropdown
				heading="More actions"
				className={styles.dropdown}
				basic={false}
				onOpenChange={this.onDropdownOpenChange}
				trigger={
					<IconButton
						className={cx(styles.dropdownKebab, { [styles.dropdownHover]: dropdownOpen })}
						title="More actions"
						icon={faEllipsisV}
					/>
				}
			>
				{this.renderDropdownItems()}
			</ArrowDropdown>
		);
	}

	render() {
		const { request, feedback, className, user, link, hideMenu, isSpecific } = this.props;
		const { id, creator, isArchived, updatedAt, lastSeenByUser, isDraft, isThreeSixtyParent } = feedback;

		let destination = `/feedback/${id}/${isDraft ? "draft/" : ""}`;
		if (request) {
			destination = `/feedback/request/${request.id}/${request.feedbackRecord ? "" : "respond/"}`;
		}

		const requestIsComplete = request && (request.feedbackRecord || request.cancelledByCreator);
		const requestBar = request && !request.isArchived;

		const updateDot =
			!isArchived &&
			!isDraft &&
			(request ? !request.lastSeenByUser : !lastSeenByUser || moment(lastSeenByUser).isSameOrBefore(updatedAt || ""));

		const updatedAtMoment = moment(updatedAt || "");

		const content = (
			<>
				<div className={cx(styles.dot, { [styles.requestBar]: requestBar, [styles.updateDot]: updateDot })} />
				{request ? (
					<FeedbackRequestSubject small request={request} user={user} isSpecific={isSpecific} />
				) : (
					<FeedbackSubject
						isThreeSixtyParent={isThreeSixtyParent}
						small
						feedback={feedback}
						user={user}
						isSpecific={isSpecific}
					/>
				)}
				{this.renderStatus()}
				{this.renderResponses()}
				<time className={styles.updated} dateTime={updatedAtMoment.format()} title={updatedAtMoment.format("LLL")}>
					{updatedAtMoment.fromNow()}
				</time>
				{creator && <Avatar className={styles.creator} user={creator} link={false} width="2em" />}
			</>
		);

		const card = link ? (
			<Link
				className={cx(styles.wrapper, styles.selectable, className, {
					[styles.archived]: request ? request.isArchived : isArchived,
				})}
				to={destination}
				data-test-id="feedback-list-card"
			>
				{content}
			</Link>
		) : (
			<div
				className={cx(styles.wrapper, className, { [styles.archived]: request ? request.isArchived : isArchived })}
				data-test-id="feedback-list-card"
			>
				{content}
			</div>
		);

		return (
			<div
				className={cx(styles.superWrapper, {
					[styles.superWrapperThreeSixty]: isThreeSixtyParent && !hideMenu,
					[styles.superWrapperThreeSixtyArchived]: request ? request.isArchived : isArchived,
				})}
			>
				{card}
				{!hideMenu && !requestIsComplete && this.renderDropdown()}
			</div>
		);
	}
}

export const FeedbackCard = withRouter(smart(FeedbackCardClass));

export default FeedbackOrRequestRenderer;
